import React, { useRef } from 'react';

import { SafeAreaView, ScrollView, StyleSheet, View } from 'react-native';

import { LayoutContainer } from 'components/layout/styled';
import LoadingAnimation from 'components/loading-animation';
import { Table } from 'components/table';
import { Box } from 'components/ui';
import { useScrollToHighlightedRow } from 'pages/loyalty/transactions/use-scroll-to-highlighted-row';
import { primitive, spacing } from 'styles/constants';
import { isNative } from 'utils/environment';
import { ITable } from 'utils/table';

import { TransactionTable, TransactionTableRow } from './types';

const Spinner = () => (
  <LayoutContainer>
    <Box style={styles.spinner}>
      <LoadingAnimation />
    </Box>
  </LayoutContainer>
);

const styles = StyleSheet.create({
  spinner: {
    padding: spacing.$2,
  },
});

export const HistoryLazyTableList = ({
  tables,
  visibleTransactions,
  handleOnPressRow,
  loadingMore,
  highlightedRowId,
  scrollViewRef,
}: {
  tables: TransactionTable[];
  visibleTransactions?: number;
  handleOnPressRow: (row: TransactionTableRow | Partial<TransactionTableRow>) => void;
  loadingMore?: boolean;
  highlightedRowId?: string;
  scrollViewRef?: React.RefObject<ScrollView | null>;
}) => {
  const rowRefs = useRef<Record<string, View | null>>({});
  useScrollToHighlightedRow(scrollViewRef, rowRefs, highlightedRowId);

  const renderItem = ({
    item,
    index,
  }: {
    item: ITable<Partial<TransactionTableRow>>;
    index: number;
  }) => (
    <Table<TransactionTableRow>
      key={`historyTable.${index}`}
      table={item}
      onPressRow={handleOnPressRow}
      getRowId={row => row.transactionId}
      getRowProps={row => {
        const rowId = row.transactionId;
        return {
          ref: (node: View | null) => {
            if (rowId) {
              rowRefs.current[rowId] = node;
            }
          },
          backgroundColor:
            rowId && rowId === highlightedRowId ? primitive.$meltyYellowOpacity20 : undefined,
        };
      }}
      marginTop="$4"
    />
  );

  // Lazy Loading only for Native...
  if (isNative && visibleTransactions) {
    return (
      <SafeAreaView style={{ flex: 1 }}>
        {tables.slice(0, visibleTransactions).map((item, index) => renderItem({ item, index }))}
        {loadingMore && <Spinner />}
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1 }}>
      {tables.map((item, index) => renderItem({ item, index }))}
    </SafeAreaView>
  );
};
